import { Injectable, inject } from '@angular/core';
import { httpsCallable } from 'firebase/functions';
import {
  doc,
  getDoc,
  getDocFromServer,
  onSnapshot,
  setDoc,
  Unsubscribe,
} from 'firebase/firestore';

import { ProductType } from '../models/account.model';
import { ProductAccessConfig } from '../models/product.model';
import { FirebaseService } from './firebase.service';

/** Maps product access IDs to account ProductType and display label */
const PRODUCT_MAP: Record<string, { accountType: ProductType; label: string }> = {
  'ai-studio': { accountType: 'studio', label: 'Forge' },
  chat: { accountType: 'chat', label: 'Chat' },
  connect: { accountType: 'connect', label: 'Connect' },
  build: { accountType: 'build', label: 'Build' },
  admin: { accountType: 'admin', label: 'Admin' },
  create: { accountType: 'create', label: 'Create' },
};

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private readonly firebase = inject(FirebaseService);

  /**
   * The org's product-access config, or `null` when the document does not exist.
   *
   * A1: `null` — not a `{ enabledProducts: ['ai-studio'] }` sentinel. The
   * sentinel produced the same answer for `'create'` (both deny), but it would
   * have wrongly *granted* `hasProduct(org, 'ai-studio')`, and "no config" is a
   * denial across the suite rather than a default grant of anything.
   */
  async getProductAccessConfig(orgId: string): Promise<ProductAccessConfig | null> {
    const cacheKey = `crewdle_product_access_${orgId}`;

    const stored = sessionStorage.getItem(cacheKey);
    if (stored) return JSON.parse(stored) as ProductAccessConfig | null;

    const configRef = doc(
      this.firebase.firestore,
      'organizations',
      orgId,
      'product-access',
      'config',
    );
    const snapshot = await getDoc(configRef);
    const config: ProductAccessConfig | null = snapshot.exists()
      ? {
          enabledProducts: snapshot.data()['enabledProducts'] || [],
          roleProductAccess: snapshot.data()['roleProductAccess'] || {},
        }
      : null;

    sessionStorage.setItem(cacheKey, JSON.stringify(config));
    return config;
  }

  /**
   * Authoritative entitlement read for route activation. Unlike
   * `getProductAccessConfig`, this bypasses sessionStorage and Firestore's local
   * cache, so a stale grant cannot authorize a revoked or offline session.
   */
  async getProductAccessConfigFromServer(orgId: string): Promise<ProductAccessConfig | null> {
    const configRef = doc(
      this.firebase.firestore,
      'organizations',
      orgId,
      'product-access',
      'config',
    );
    const snapshot = await getDocFromServer(configRef);
    const config: ProductAccessConfig | null = snapshot.exists()
      ? {
          enabledProducts: snapshot.data()['enabledProducts'] || [],
          roleProductAccess: snapshot.data()['roleProductAccess'] || {},
        }
      : null;
    sessionStorage.setItem(`crewdle_product_access_${orgId}`, JSON.stringify(config));
    return config;
  }

  clearProductAccessCache(orgId: string): void {
    sessionStorage.removeItem(`crewdle_product_access_${orgId}`);
  }

  /**
   * Subscribe to product access config changes. Fires once on attach with
   * the current Firestore value, then again on every remote update (e.g.
   * agent-driven toggles). Updates the sessionStorage cache on each fire.
   */
  subscribeToConfig(
    orgId: string,
    onChange: (config: ProductAccessConfig | null) => void,
  ): Unsubscribe {
    const configRef = doc(
      this.firebase.firestore,
      'organizations',
      orgId,
      'product-access',
      'config',
    );
    return onSnapshot(configRef, (snapshot) => {
      const config: ProductAccessConfig | null = snapshot.exists()
        ? {
            enabledProducts: snapshot.data()['enabledProducts'] || [],
            roleProductAccess: snapshot.data()['roleProductAccess'] || {},
          }
        : null;
      sessionStorage.setItem(`crewdle_product_access_${orgId}`, JSON.stringify(config));
      onChange(config);
    });
  }

  async saveProductAccessConfig(orgId: string, config: ProductAccessConfig): Promise<void> {
    const configRef = doc(
      this.firebase.firestore,
      'organizations',
      orgId,
      'product-access',
      'config',
    );
    await setDoc(configRef, {
      enabledProducts: config.enabledProducts,
      roleProductAccess: config.roleProductAccess,
    });
    sessionStorage.removeItem(`crewdle_product_access_${orgId}`);
  }

  /**
   * Ensures an account exists for the given product.
   *
   * Product accounts (and their API keys) are created ONLY by the backend: the
   * browser never generates, reads or writes vendor secrets. When the org has no
   * account for this product yet, the server `provisionProductAccount` callable
   * provisions it. That callable is currently admin-only, so for a non-admin we
   * surface a clear "ask your admin" error instead of attempting any client write.
   * @param productId - The product access ID (e.g. 'ai-studio', 'chat')
   */
  async ensureProductAccount(orgId: string, productId: string): Promise<void> {
    const mapping = PRODUCT_MAP[productId];
    if (!mapping) return;

    const orgSnap = await getDoc(doc(this.firebase.firestore, 'organizations', orgId));
    const accounts = orgSnap.data()?.['accounts'] || {};
    if (accounts[mapping.accountType]) return;

    try {
      await httpsCallable<{ organizationId: string; productId: string }, { accountId: string | null }>(
        this.firebase.functions,
        'provisionProductAccount',
      )({ organizationId: orgId, productId });
    } catch (error) {
      const code = (error as { code?: string })?.code ?? '';
      if (code.endsWith('permission-denied')) {
        throw new ProductAccountUnavailableError(mapping.label);
      }
      throw error;
    }
  }
}

/** The org has no account for a product yet and the caller cannot provision it. */
export class ProductAccountUnavailableError extends Error {
  constructor(readonly productLabel: string) {
    super(`Your organization's ${productLabel} account has not been set up yet. Ask your organization admin to set it up.`);
    this.name = 'ProductAccountUnavailableError';
  }
}
